import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import {
  getMoveAllToAppLocationStatus,
  setMoveAllToAppLocationJob,
} from "../../../actions/products";
import { setGlobalAlert } from "../../../actions/common";
import "../styles.scss";

export const MOVE_ALL_TO_APP_LOCATION_JOB_KEY =
  "riverr.listingsMoveAllToAppLocationJob";
const POLL_MS = 3000;

const MoveAllToAppLocationJobWatcher = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const job = useSelector((state) => state?.products?.moveAllToAppLocationJob);
  const pollTimerRef = useRef(null);
  const finishingRef = useRef(false);
  const [floatHidden, setFloatHidden] = useState(false);
  const onListingsIndex =
    location.pathname === "/listings" || location.pathname === "/listings/";

  const clearPoll = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const clearStoredJob = () => {
    try {
      localStorage.removeItem(MOVE_ALL_TO_APP_LOCATION_JOB_KEY);
    } catch (_) {
      /* ignore */
    }
  };

  const finishJob = (status) => {
    if (finishingRef.current) return;
    finishingRef.current = true;
    clearPoll();
    clearStoredJob();
    dispatch(setMoveAllToAppLocationJob(status));

    const failed = status?.failed || 0;
    const succeeded = status?.succeeded || 0;
    const isHardFail =
      status?.status === "failed" || (failed > 0 && succeeded === 0);

    if (isHardFail) {
      dispatch(
        setGlobalAlert({
          type: "error",
          message:
            status?.error || "Could not move products to the app location.",
        })
      );
    } else if (failed > 0) {
      dispatch(
        setGlobalAlert({
          type: "warning",
          message:
            status?.error ||
            `${succeeded} variants moved, ${failed} failed.`,
        })
      );
    } else {
      dispatch(
        setGlobalAlert({
          type: "success",
          message: "All products have been moved to the app location.",
        })
      );
    }

    finishingRef.current = false;
  };

  const pollJob = async (jobId) => {
    try {
      const res = await getMoveAllToAppLocationStatus(jobId);
      const status = res?.data;
      if (!status) throw new Error("Unable to read move progress.");

      dispatch(setMoveAllToAppLocationJob(status));

      if (status.status === "processing") {
        pollTimerRef.current = setTimeout(() => pollJob(jobId), POLL_MS);
        return;
      }

      finishJob(status);
    } catch (err) {
      clearPoll();
      clearStoredJob();
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Unable to check move progress.";
      dispatch(
        setMoveAllToAppLocationJob({
          jobId,
          status: "failed",
          error: message,
          total: 0,
          completed: 0,
          succeeded: 0,
          failed: 0,
        })
      );
      dispatch(setGlobalAlert({ type: "error", message }));
    }
  };

  useEffect(() => {
    let jobId = null;
    try {
      jobId = localStorage.getItem(MOVE_ALL_TO_APP_LOCATION_JOB_KEY);
    } catch (_) {
      /* ignore */
    }
    if (jobId) {
      dispatch(
        setMoveAllToAppLocationJob({
          jobId,
          status: "processing",
          total: 0,
          completed: 0,
          succeeded: 0,
          failed: 0,
        })
      );
    }
  }, [dispatch]);

  useEffect(() => {
    const jobId = job?.jobId;
    if (!jobId || job?.status !== "processing") {
      return clearPoll;
    }

    setFloatHidden(false);
    clearPoll();
    pollJob(jobId);
    return clearPoll;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.jobId, job?.status === "processing"]);

  if (!job || job.status !== "processing" || floatHidden || onListingsIndex) {
    return null;
  }

  const total = job.total || 0;
  const completed = job.completed || 0;
  const percent =
    total > 0 ? Math.min(100, Math.round((completed / total) * 100)) : 0;

  return (
    <div className="listings-bulk-ignore-float" role="status" aria-live="polite">
      <strong>
        {job.currentShopName
          ? `Moving ${job.currentShopName}`
          : "Moving products to app location"}
      </strong>
      <div>
        {completed} of {total || "…"} · {total > 0 ? `${percent}%` : "running"}
      </div>
      <div className="listings-bulk-ignore-float__track">
        <div
          className="listings-bulk-ignore-float__fill"
          style={{ width: `${percent}%` }}
        />
      </div>
      <button
        type="button"
        className="listings-bulk-ignore-float__dismiss"
        onClick={() => setFloatHidden(true)}
      >
        Hide
      </button>
    </div>
  );
};

export default MoveAllToAppLocationJobWatcher;
